import { DatePipe } from "@angular/common";
import { ChangeDetectionStrategy, Component, computed, inject, input, signal } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { FieldTree, FormField } from "@angular/forms/signals";
import { DspFunnelDescriptions } from "@front/m19-models";
import { AuthService } from "@front/m19-services";
import { IBadgeComponent, ISelectMenuImports } from "@front/m19-ui";
import { TranslocoDirective } from "@jsverse/transloco";
import { StrategyStateComponent } from "@m19-board/shared/strategy-state/strategy-state.component";
import { DeliveredAmcAnalysisStrategy } from "@m19-board/amc/amc-strategy-analysis-service/amc-strategy-analysis.service";

export interface AmcAnalysisStrategyModel {
  strategyId: number | null;
}

@Component({
  selector: "app-amc-analysis-strategy-step",
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TranslocoDirective, ISelectMenuImports, IBadgeComponent, FormField, StrategyStateComponent, DatePipe],
  template: `<div class="flex flex-col gap-6 py-8" *transloco="let t">
    <div class="flex flex-col gap-1">
      <h4>{{ t("common.strategy") }}</h4>
      <p class="text-muted-foreground m-0">{{ t("amc.strategy_analysis_strategy_description") }}</p>
    </div>
    <ISelectMenu class="w-full" fitTriggerWidth [formField]="strategy().strategyId" [(searchQuery)]="search">
      <ISelectTrigger>
        <ISelectValue [placeholder]="t('amc.strategy_analysis_select_strategy')">
          <div *iSelectValue>{{ selectedStrategy()?.name }}</div>
        </ISelectValue>
      </ISelectTrigger>
      <ISelectContent>
        @for (amcStrategy of filteredStrategies(); track amcStrategy.strategyId) {
          <ISelectOption [value]="amcStrategy.strategyId">
            <div class="flex w-full items-center justify-between gap-2 pr-1">
              <div class="flex items-center gap-2">
                <app-strategy-state [state]="amcStrategy.state" /> <span class="truncate">{{ amcStrategy.name }}</span>
              </div>
              <div class="flex flex-col items-end justify-between gap-2">
                @if (amcStrategy.funnel) {
                  <IBadge
                    [label]="t(FunnelDescriptions[amcStrategy.funnel].title)"
                    variant="soft"
                    color="white"
                    size="xs"
                  />
                }
                @if (amcStrategy.budgetFlight; as flight) {
                  <div class="type-caption">
                    {{ flight.startDate | date: "mediumDate" : undefined : locale() }} -
                    {{ flight.endDate | date: "mediumDate" : undefined : locale() }}
                  </div>
                }
              </div>
            </div>
          </ISelectOption>
        }
      </ISelectContent>
    </ISelectMenu>
  </div>`,
})
export class AmcAnalysisStrategyStepComponent {
  private readonly authService = inject(AuthService);

  readonly strategy = input.required<FieldTree<AmcAnalysisStrategyModel>>();
  readonly strategies = input.required<DeliveredAmcAnalysisStrategy[]>();

  readonly FunnelDescriptions = DspFunnelDescriptions;
  readonly search = signal("");
  readonly locale = toSignal(this.authService.userLocale$, { requireSync: true });

  readonly selectedStrategy = computed(() =>
    this.strategies().find((s) => s.strategyId === this.strategy().strategyId().value()),
  );
  readonly filteredStrategies = computed(() => {
    const search = this.search().toLowerCase();
    return this.strategies().filter((s) => s.name?.toLowerCase().includes(search));
  });
}
